import * as React from 'react';
import pluralize from 'pluralize';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {Link} from 'react-router-dom';
import {
  Button,
  ButtonVariant,
  ButtonWidth,
} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import {useCountDownSeconds} from '@practicegenius/pg-ui/src/hooks/useCountDown';
import {routes} from '../routes';

type ResponseAction = 'account_paused' | 'help' | 'pause_warning';

const titles = {
  account_paused: 'Whoops!',
  help: 'Hey There!',
  pause_warning: 'Hold On!',
};

type Props = {
  isOpen: boolean;
  close: () => unknown;
  formValues: any;
  responseAction: ResponseAction;
  availableIn: number;
  nextBlockDuration: number;
};

const humanizeMinutes = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);

  return `${minutes} ${pluralize('minute', minutes)}`;
};

const humanizeSeconds = (seconds: number) => {
  if (seconds < 60) {
    return `${seconds} ${pluralize('second', seconds)}`;
  }

  if (seconds < 180) {
    return `${humanizeMinutes(seconds)} and ${seconds % 60} ${pluralize(
      'second',
      seconds,
    )}`;
  }

  return humanizeMinutes(seconds);
};

const HelpContent = (props: Props) => (
  <>
    It looks like you are having trouble logging in. Can we help you out? Use a
    link to recover your username or password.
  </>
);

const PauseWarningContent = (props: Props) => (
  <>
    Something's not quite right. To keep your account safe it will be paused for{' '}
    <strong>{humanizeSeconds(props.nextBlockDuration)}</strong> after the next
    failed login.
    <br />
    <br />
    Don't worry. We can help you recover your username or password. Click a link
    below.
  </>
);

const AccountPausedContent = ({availableIn, close}: Props) => {
  const displaySeconds = useCountDownSeconds(availableIn);

  React.useEffect(() => {
    if (displaySeconds <= 0) {
      close();
    }
  }, [displaySeconds, close]);

  return (
    <>
      Let's take a step back. To make sure your information is safe, your
      account has been paused for{' '}
      <strong>{humanizeSeconds(displaySeconds)}</strong>.
      <br />
      <br />
      You can try logging in again soon. We can still help you recover your
      username or password. Click a link below.
    </>
  );
};

const messages = {
  help: HelpContent,
  pause_warning: PauseWarningContent,
  account_paused: AccountPausedContent,
};

export function UserRateLimitedModal(props: Props) {
  const {isOpen, close, responseAction, formValues} = props;

  // eslint-disable-next-line no-prototype-builtins
  const title = titles.hasOwnProperty(responseAction)
    ? titles[responseAction]
    : null;
  // eslint-disable-next-line no-prototype-builtins
  const message = messages.hasOwnProperty(responseAction)
    ? messages[responseAction](props)
    : null;

  return (
    <Dialog open={isOpen} onClose={close}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Box marginRight="auto">
          <Button
            variant={ButtonVariant.text}
            color="secondary"
            width={ButtonWidth.WIDE}
            onClick={close}
          >
            Try Again
          </Button>
        </Box>
        <Button
          component={Link}
          // @ts-expect-error using prop from Link
          to={{
            pathname: routes.login.forgotUsername,
            state: formValues,
          }}
          variant={ButtonVariant.text}
          color="primary"
          width={ButtonWidth.WIDE}
        >
          Forgot Username
        </Button>
        <Button
          component={Link}
          // @ts-expect-error using prop from Link
          to={{
            pathname: routes.login.forgotPassword,
            state: formValues,
          }}
          variant={ButtonVariant.text}
          color="primary"
          width={ButtonWidth.WIDE}
        >
          Forgot Password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
